/**
 * Push notifications in the iOS app (1.1+). Server side: server/customer-push.js.
 *
 * iOS shows its permission prompt only once, so the app asks at a moment that
 * says why (NativeTripsPrompt on Trips, or Profile → Notifications), never at
 * launch. With permission, the app registers with Apple and hands the device
 * token to /api/push/devices for the signed-in customer, again on every launch
 * and sign-in, since tokens change. Signing out takes this phone off the
 * account, so the next person to sign in here doesn't get the last one's news.
 * Turning notifications off in Profile does the same and keeps them off on
 * this phone until they're turned back on or the customer signs out, which
 * clears the app's storage (iOS has no way for an app to take back its own
 * permission; iPhone Settings is where they stay off for good).
 *
 * Tapping a notification opens the page in its `path`, usually the trip.
 * On the web every function here is a no-op.
 */
import { isNativeApp } from '@/native/bootstrap';
import { duringSystemPrompt } from '@/native/faceId';

const TOKEN_KEY = 'defera.push.token.v1';
const ASKED_KEY = 'defera.push.asked.v1';
const OFF_KEY = 'defera.push.off.v1';
const loadPush = () => import('@capacitor/push-notifications');

const stored = {
  get: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  set: (key, value) => { try { localStorage.setItem(key, value); } catch { /* private browsing */ } },
  remove: (key) => { try { localStorage.removeItem(key); } catch { /* private browsing */ } },
};

export const hasBeenAskedAboutPush = () => stored.get(ASKED_KEY) === '1';
export const markAskedAboutPush = () => stored.set(ASKED_KEY, '1');
export const pushTurnedOffHere = () => stored.get(OFF_KEY) === '1';
/** This phone's token is on the signed-in account. */
export const pushRegisteredHere = () => Boolean(stored.get(TOKEN_KEY));

/** 'granted', 'denied', 'prompt' (iOS can still ask) or 'unavailable'. */
export async function pushPermission({ native = isNativeApp, load = loadPush } = {}) {
  if (!native) return 'unavailable';
  try {
    const { PushNotifications } = await load();
    const { receive } = await PushNotifications.checkPermissions();
    if (receive === 'granted' || receive === 'denied') return receive;
    return 'prompt';
  } catch {
    return 'unavailable';
  }
}

/** Register with Apple: the device token, or null on an error or after 10 seconds. */
function registerForToken(PushNotifications, timeoutMs = 10_000) {
  return new Promise((resolve) => {
    let done = false;
    const handles = [];
    let timer = null;
    const finish = (token) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      handles.forEach((handle) => handle?.remove?.());
      resolve(token || null);
    };
    timer = setTimeout(() => finish(null), timeoutMs);
    Promise.all([
      PushNotifications.addListener('registration', (t) => finish(t?.value)),
      PushNotifications.addListener('registrationError', () => finish(null)),
    ])
      .then((added) => { handles.push(...added); if (done) added.forEach((h) => h?.remove?.()); return PushNotifications.register(); })
      .catch(() => finish(null));
  });
}

async function accessToken() {
  const { supabase } = await import('@/lib/supabase');
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

async function appVersion() {
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    return `${info.version} (${info.build})`;
  } catch {
    return null;
  }
}

async function saveToken(token, fetchImpl) {
  const auth = await accessToken();
  if (!auth) return false;
  const res = await fetchImpl('/api/push/devices', {
    method: 'POST',
    headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, app_version: await appVersion() }),
  });
  if (res.ok) stored.set(TOKEN_KEY, token);
  return res.ok;
}

/** Ask if iOS still can, register, and save. Resolves 'enabled', 'denied' or 'error'. */
export async function enablePushNotifications({ native = isNativeApp, load = loadPush, fetchImpl = (...a) => fetch(...a) } = {}) {
  if (!native) return 'error';
  markAskedAboutPush();
  stored.remove(OFF_KEY);
  try {
    const { PushNotifications } = await load();
    let { receive } = await PushNotifications.checkPermissions();
    if (receive !== 'granted' && receive !== 'denied') ({ receive } = await duringSystemPrompt(() => PushNotifications.requestPermissions()));
    if (receive !== 'granted') return 'denied';
    const token = await registerForToken(PushNotifications);
    if (!token) return 'error';
    return (await saveToken(token, fetchImpl)) ? 'enabled' : 'error';
  } catch {
    return 'error';
  }
}

/** Launch and sign-in: with permission already given, put this phone on the signed-in account. */
export async function syncPushRegistration({ native = isNativeApp, load = loadPush, fetchImpl = (...a) => fetch(...a) } = {}) {
  if (!native || pushTurnedOffHere()) return;
  try {
    const { PushNotifications } = await load();
    const { receive } = await PushNotifications.checkPermissions();
    if (receive !== 'granted') return;
    const token = await registerForToken(PushNotifications);
    if (token) await saveToken(token, fetchImpl);
  } catch {
    // Next launch tries again.
  }
}

/** Sign-out: take this phone off the account. Best effort, at most 3 seconds. */
export async function forgetThisPhone({ native = isNativeApp, fetchImpl = (...a) => fetch(...a) } = {}) {
  if (!native) return;
  const token = stored.get(TOKEN_KEY);
  if (!token) return;
  try {
    const auth = await accessToken();
    if (auth) {
      let timer;
      await Promise.race([
        fetchImpl('/api/push/devices', {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        }),
        new Promise((resolve) => { timer = setTimeout(resolve, 3000); }),
      ]).finally(() => clearTimeout(timer));
    }
  } catch {
    // The server stops using tokens Apple reports as gone.
  }
  stored.remove(TOKEN_KEY);
}

/** Profile → Notifications off: no more notifications on this phone until turned back on. */
export async function turnOffPushHere(options = {}) {
  stored.set(OFF_KEY, '1');
  await forgetThisPhone(options);
}

/** An in-app page from a notification, or null for anything else. */
export function notificationPath(action) {
  const path = action?.notification?.data?.path;
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') ? path : null;
}

/** Tapping a notification opens its page. Resolves a function that removes the listener. */
export async function onNotificationTap(navigate, { native = isNativeApp, load = loadPush } = {}) {
  if (!native) return () => {};
  try {
    const { PushNotifications } = await load();
    const handle = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const path = notificationPath(action);
      if (path) navigate(path);
    });
    return () => handle?.remove?.();
  } catch {
    return () => {};
  }
}
